import config from '../../config/config';

const getProductDataKeys = (allData, dataKeyToValueMap) => {
  if (!allData || allData.length === 0) return { keys: [], mappedHeaders: [] };
  
  const baseKeys = ['eu_pnumber', 'eu_brand_name_current', 'active_substance_current', 'eu_aut_date'];
  const keys = Object.keys(allData[0]).filter(key => !baseKeys.includes(key));
  
  const mappedHeaders = keys.map(key => {
    const niceName = dataKeyToValueMap[key] || key;
    return `"${niceName.replace(/"/g, '""')}"`;
  });
  const excelMappedHeaders = keys.map(key => dataKeyToValueMap[key] || key);

  return { keys, csvMappedHeaders: mappedHeaders, excelMappedHeaders };
};

const getProductDataForRow = (productData, productDataKeys) => {
  return productDataKeys.map(key => productData[key] || '');
};

const getCsvProductDataForRow = (productData, productDataKeys) => {
  return productDataKeys.map(key => {
    const value = productData[key];
    return value ? `"${value.toString().replace(/"/g, '""')}"` : '""';
  }).join(',');
};

// FIX: Build an O(1) lookup map from eu_pnumber -> productData.
// Previously allData.find() was called inside the per-medicine loop = O(n*m).
const buildProductDataMap = (allData) => {
  const map = new Map();
  if (!allData) return map;
  for (const data of allData) {
    const normalised = data.eu_pnumber.replace(/-/g, '/');
    map.set(normalised, data);
  }
  return map;
};


export const exportToCSVUtility = (
  filteredAndSortedGroups,
  selectedMedicines,
  dataKeyToValueMap,
  allData,
  includeProductData,
  includeTermFrequencies,
  displayedSearchQuery
) => {
  const selectedItems = filteredAndSortedGroups.filter(group => selectedMedicines[group.euNumber]);
  if (selectedItems.length === 0) {
    alert('Please select at least one medicine to export');
    return;
  }

  // Collect all unique terms
  const allUniqueTerms = new Set();
  selectedItems.forEach(item => {
    const docGroups = Object.values(item.documentsMap || {});
    docGroups.forEach(docGroup => {
      const docDetails = docGroup.details;
      const images = docGroup.images || [];
      (docDetails.matched_terms || []).forEach(([term]) => allUniqueTerms.add(term));
      images.forEach(image => {
        (image.matched_terms || []).forEach(([term]) => allUniqueTerms.add(term));
      });
    });
  });
  const uniqueTermsArray = Array.from(allUniqueTerms).sort();

  const euNumberLabel = dataKeyToValueMap['eu_pnumber'] || 'EU Number';
  const brandNameLabel = dataKeyToValueMap['eu_brand_name_current'] || 'Brand Name';
  const activeSubstanceLabel = dataKeyToValueMap['active_substance_current'] || 'Active Substance';
  const euAuthDateLabel = dataKeyToValueMap['eu_aut_date'] || 'EU Authorization Date';

  let headers = `"${euNumberLabel}","${brandNameLabel}","${activeSubstanceLabel}","${euAuthDateLabel}","Document Count","Document Type","Document Name","Document Link","Hit Type","Image Page Number","Product Page Link"`;

  if (includeTermFrequencies) {
    uniqueTermsArray.forEach(term => {
      headers += `,"${term.replace(/"/g, '""')}"`;
    });
  }

  const { keys: productKeys, csvMappedHeaders } = includeProductData 
    ? getProductDataKeys(allData, dataKeyToValueMap) 
    : { keys: [], csvMappedHeaders: [] };

  if (includeProductData && csvMappedHeaders.length > 0) {
    headers += "," + csvMappedHeaders.join(',');
  }

  // Pre-build suffix of zero-frequency term columns (reused for empty-doc rows)
  const zeroTermsSuffix = includeTermFrequencies ? ',' + uniqueTermsArray.map(() => '0').join(',') : '';

  // FIX: Build lookup map once — O(m) — instead of find() per item — O(n*m)
  const productDataMap = buildProductDataMap(allData);

  const rows = [headers];

  selectedItems.forEach(item => {
    const productPageUrl = `${window.location.origin}${config.routes.main.details}/${item.euNumber.replace(/-/g, '_')}`;
    const formattedEuNumber = item.euNumber.replace(/-/g, '/');

    // FIX: O(1) map lookup instead of O(m) find()
    const productData = productDataMap.get(formattedEuNumber) || {};
    const euAuthDate = productData['eu_aut_date'] || '';

    const escapedMedicineName = `"${item.medicineName.replace(/"/g, '""')}"`;
    const escapedActiveSubstance = `"${item.activeSubstance.replace(/"/g, '""')}"`;

    // FIX: Compute product data columns once per medicine, reuse for every row
    const productDataSuffix = (includeProductData && productKeys.length > 0)
      ? ',' + getCsvProductDataForRow(productData, productKeys)
      : '';

    const medicinePrefix = `${formattedEuNumber},${escapedMedicineName},${escapedActiveSubstance},"${euAuthDate}",${item.documentCount}`;

    const docGroups = Object.values(item.documentsMap || {});

    if (docGroups.length === 0) {
      rows.push(`${medicinePrefix},"","","","Text","","${productPageUrl}"${zeroTermsSuffix}${productDataSuffix}`);
    } else {
      docGroups.forEach((docGroup) => {
        const docDetails = docGroup.details;
        const docImages = docGroup.images || [];

        const fileType = docDetails.file_type === 'Nan' || !docDetails.file_type ? 'Other' : docDetails.file_type;
        const escapedDocName = docDetails.real_name ? `"${docDetails.real_name.replace(/"/g, '""')}"` : '""';
        const docLink = docDetails.file_link && docDetails.file_link !== 'Nan' ? `"${docDetails.file_link}"` : '""';

        const docPrefix = `${medicinePrefix},"${fileType}",${escapedDocName},${docLink}`;

        // Text hit row
        let termsSuffix = '';
        if (includeTermFrequencies) {
          const termsMap = new Map(docDetails.matched_terms || []);
          termsSuffix = ',' + uniqueTermsArray.map(term => termsMap.get(term) || 0).join(',');
        }
        rows.push(`${docPrefix},"Text","","${productPageUrl}"${termsSuffix}${productDataSuffix}`);

        // Image hit rows
        docImages.forEach(image => {
          const imagePageNumber = image.page !== null && image.page !== undefined ? `"${image.page}"` : '""';
          let imgTermsSuffix = '';
          if (includeTermFrequencies) {
            const termsMap = new Map(image.matched_terms || []);
            imgTermsSuffix = ',' + uniqueTermsArray.map(term => termsMap.get(term) || 0).join(',');
          }
          rows.push(`${docPrefix},"Image",${imagePageNumber},"${productPageUrl}"${imgTermsSuffix}${productDataSuffix}`);
        });
      });
    }
  });

  const csvContent = rows.join('\n');
  const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `docshark-export-${new Date().toISOString().slice(0, 10)}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const exportToExcelUtility = async (
  filteredAndSortedGroups,
  selectedMedicines,
  dataKeyToValueMap,
  allData,
  includeProductData,
  includeTermFrequencies,
  groupedResults,
  displayedSearchQuery
) => {
  const XLSX = await import('xlsx');
  const selectedItems = filteredAndSortedGroups.filter(group => selectedMedicines[group.euNumber]);
  if (selectedItems.length === 0) {
    alert('Please select at least one medicine to export');
    return;
  }

  // Collect all unique terms
  const allUniqueTerms = new Set();
  selectedItems.forEach(item => {
    const docGroups = Object.values(item.documentsMap || {});
    docGroups.forEach(docGroup => {
      const docDetails = docGroup.details;
      const images = docGroup.images || [];
      (docDetails.matched_terms || []).forEach(([term]) => allUniqueTerms.add(term));
      images.forEach(image => {
        (image.matched_terms || []).forEach(([term]) => allUniqueTerms.add(term));
      });
    });
  });
  const uniqueTermsArray = Array.from(allUniqueTerms).sort();

  const euNumberLabel = dataKeyToValueMap['eu_pnumber'] || 'EU Number';
  const brandNameLabel = dataKeyToValueMap['eu_brand_name_current'] || 'Brand Name';
  const activeSubstanceLabel = dataKeyToValueMap['active_substance_current'] || 'Active Substance';
  const euAuthDateLabel = dataKeyToValueMap['eu_aut_date'] || 'EU Authorization Date';

  const { keys: productKeys, excelMappedHeaders } = includeProductData 
    ? getProductDataKeys(allData, dataKeyToValueMap)
    : { keys: [], excelMappedHeaders: [] };

  const headers = [
    euNumberLabel, brandNameLabel, activeSubstanceLabel, euAuthDateLabel,
    'Document Count', 'Document Type', 'Document Name', 'Document Link', 
    'Hit Type', 'Image Page Number', 'Product Page Link'
  ];

  if (includeTermFrequencies) headers.push(...uniqueTermsArray);
  // Product data columns are NO LONGER inlined here — they go on a dedicated sheet instead.

  const mainData = [headers];

  // FIX: Build lookup map once — O(m) — instead of find() per item — O(n*m)
  const productDataMap = buildProductDataMap(allData);

  // Pre-build zero-terms array (reused for empty-doc rows)
  const zeroTermsArray = includeTermFrequencies ? uniqueTermsArray.map(() => 0) : [];

  const processedDocsForCounting = new Set();
  let totalUniqueDocumentsExported = 0;

  // Track which cells need date formatting: { rowIndex -> true }
  // We'll collect date strings and convert after to avoid iterating all cells
  const dateRowIndices = [];

  selectedItems.forEach(item => {
    const productPageUrl = `${window.location.origin}${config.routes.main.details}/${item.euNumber.replace(/-/g, '_')}`;
    const formattedEuNumber = item.euNumber.replace(/-/g, '/');

    // FIX: O(1) map lookup
    const productData = productDataMap.get(formattedEuNumber) || {};
    const euAuthDateRaw = productData['eu_aut_date'] || '';

    // FIX: Parse the date once per medicine, reuse across all its rows
    let euAuthDateValue = euAuthDateRaw;
    let needsDateFormat = false;
    if (euAuthDateRaw && /^\d{4}-\d{2}-\d{2}$/.test(euAuthDateRaw)) {
      const [y, m, d] = euAuthDateRaw.split('-');
      const date = new Date(Date.UTC(parseInt(y), parseInt(m) - 1, parseInt(d)));
      if (!isNaN(date.getTime())) {
        euAuthDateValue = date;
        needsDateFormat = true;
      }
    }

    // Product data goes on a separate sheet — not computed per-row anymore.

    const docGroups = Object.values(item.documentsMap || {});

    if (docGroups.length === 0) {
      const row = [
        formattedEuNumber, item.medicineName, item.activeSubstance, euAuthDateValue,
        item.documentCount, '', '', '', 'Text', '', productPageUrl,
        ...zeroTermsArray,
      ];
      if (needsDateFormat) dateRowIndices.push(mainData.length);
      mainData.push(row);
    } else {
      docGroups.forEach(docGroup => {
        const docDetails = docGroup.details;
        const docImages = docGroup.images || [];
        const docKey = `${item.euNumber}_${docDetails.original_pdf_filename || docDetails.filename}`;
        if (!processedDocsForCounting.has(docKey)) {
          totalUniqueDocumentsExported++;
          processedDocsForCounting.add(docKey);
        }

        const fileType = docDetails.file_type === 'Nan' || !docDetails.file_type ? 'Other' : docDetails.file_type;
        const docName = docDetails.real_name || docDetails.original_pdf_filename || docDetails.filename || '';
        const docLink = docDetails.file_link && docDetails.file_link !== 'Nan' ? docDetails.file_link : '';

        // Text hit row — term frequencies
        const textTermCols = includeTermFrequencies
          ? (() => {
              const termsMap = new Map(docDetails.matched_terms || []);
              return uniqueTermsArray.map(term => termsMap.get(term) || 0);
            })()
          : [];

        const docRow = [
          formattedEuNumber, item.medicineName, item.activeSubstance, euAuthDateValue,
          item.documentCount, fileType, docName, docLink,
          'Text', '', productPageUrl,
          ...textTermCols,
        ];
        if (needsDateFormat) dateRowIndices.push(mainData.length);
        mainData.push(docRow);

        // Image hit rows
        docImages.forEach(image => {
          const imagePageNumber = image.page !== null && image.page !== undefined ? image.page : '';
          const imgTermCols = includeTermFrequencies
            ? (() => {
                const termsMap = new Map(image.matched_terms || []);
                return uniqueTermsArray.map(term => termsMap.get(term) || 0);
              })()
            : [];

          const imageRow = [
            formattedEuNumber, item.medicineName, item.activeSubstance, euAuthDateValue,
            item.documentCount, fileType, docName, docLink,
            'Image', imagePageNumber, productPageUrl,
            ...imgTermCols,
          ];
          if (needsDateFormat) dateRowIndices.push(mainData.length);
          mainData.push(imageRow);
        });
      });
    }
  });

  const metadataData = [
    ['DocShark Export Metadata'],
    ['Export Date', new Date().toLocaleString()],
    ['Search Query', displayedSearchQuery || 'N/A'],
    ['Total Medicines Exported', selectedItems.length],
    ['Total Unique Documents Exported', totalUniqueDocumentsExported],
    ['EMRD Product Data Included', includeProductData ? 'Yes' : 'No'],
    ['Term Frequencies Included', includeTermFrequencies ? `Yes (${uniqueTermsArray.length} unique terms)` : 'No']
  ];

  const mainWS = XLSX.utils.aoa_to_sheet(mainData);
  const metadataWS = XLSX.utils.aoa_to_sheet(metadataData);

  // FIX: Apply date formatting only to the cells that actually need it (column index 3),
  // using the pre-collected row indices — avoids iterating every cell in the sheet.
  dateRowIndices.forEach(rowIdx => {
    const cellAddress = XLSX.utils.encode_cell({ r: rowIdx, c: 3 });
    const cell = mainWS[cellAddress];
    if (cell && cell.v instanceof Date) {
      cell.t = 'd';
      cell.z = 'dd/mm/yyyy';
    }
  });

  // Table Formatting: Enable AutoFilter and Freeze Top Row
  const range = XLSX.utils.decode_range(mainWS['!ref']);
  mainWS['!autofilter'] = { ref: XLSX.utils.encode_range(range) };
  mainWS['!views'] = [{ state: 'frozen', ySplit: 1 }];

  const mainCols = [
    { wch: 15 }, { wch: 30 }, { wch: 30 }, { wch: 20 }, { wch: 15 },
    { wch: 15 }, { wch: 40 }, { wch: 50 },
    { wch: 10 }, { wch: 18 }, { wch: 50 }
  ];
  if (includeTermFrequencies) uniqueTermsArray.forEach(() => mainCols.push({ wch: 10 }));
  mainWS['!cols'] = mainCols;

  const metadataCols = [{ wch: 30 }, { wch: 30 }];
  metadataWS['!cols'] = metadataCols;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, mainWS, 'Data');
  XLSX.utils.book_append_sheet(wb, metadataWS, 'Metadata');

  // OPTIMISATION: Write EMRD product data to a dedicated sheet — one row per medicine —
  // instead of repeating all product columns on every document/image row in the main sheet.
  // This mirrors how Excel compresses it internally, so the file stays small on disk too.
  if (includeProductData && productKeys.length > 0) {
    const emrdHeaders = [euNumberLabel, ...excelMappedHeaders];
    const emrdData = [emrdHeaders];

    selectedItems.forEach(item => {
      const formattedEuNumber = item.euNumber.replace(/-/g, '/');
      const productData = productDataMap.get(formattedEuNumber) || {};
      emrdData.push([formattedEuNumber, ...getProductDataForRow(productData, productKeys)]);
    });

    const emrdWS = XLSX.utils.aoa_to_sheet(emrdData);
    const emrdRange = XLSX.utils.decode_range(emrdWS['!ref']);
    emrdWS['!autofilter'] = { ref: XLSX.utils.encode_range(emrdRange) };
    emrdWS['!views'] = [{ state: 'frozen', ySplit: 1 }];
    emrdWS['!cols'] = [{ wch: 15 }, ...productKeys.map(() => ({ wch: 25 }))];
    XLSX.utils.book_append_sheet(wb, emrdWS, 'EMRD Product Data');
  }

  // OPTIMISATION: compression: true applies DEFLATE to the XML inside the xlsx zip.
  // Repeated strings (medicine names, active substances, doc types, URLs) compress
  // extremely well — typically 60–80% size reduction on repetitive exports.
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array', compression: true });
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `docshark-export-${new Date().toISOString().slice(0, 10)}.xlsx`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const makeEMRDselectionUtility = (
  selectedMedicines,
  checkedState,
  setCheckedState
) => {
  const newCheckedState = Object.fromEntries(
    Object.keys(checkedState).map(key => [key, false])
  );

  const medicinesToSelect = Object.keys(selectedMedicines);

  medicinesToSelect.forEach(key => {
    const formattedKey = key.replaceAll('-', '/');
    if (Object.hasOwn(newCheckedState, formattedKey)) {
      newCheckedState[formattedKey] = true;
    } else {
      console.warn(`Key "${formattedKey}" not found in checkedState.`);
    }
  });
  
  setCheckedState(newCheckedState);
};